import { Fragment } from 'react';
import {
  CircleDashed,
  CircleHelp,
  Fingerprint,
  KeyRound,
  ShieldAlert,
  ShieldCheck,
  Unplug,
} from 'lucide-react';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { formatDateTime } from '@/lib/format';

/**
 * Zugangsstatus eines Systems - "komme ich da ueberhaupt drauf?".
 *
 * Anlass (13.08.2026, Tischlerei Koeck): vier Läufe, zwei rot. Auf zwei Hosts
 * lag kein SSH-Schlüssel, auf einem fehlte die Bitwarden-Sitzung. Das sind
 * Zugangslücken, keine Softwarefehler - aber das Tool sagte sie erst
 * HINTERHER als roten Balken. Diese Marke sagt es VORHER, bei der Auswahl.
 *
 * ADR-011: Der Server testet NIE selbst. Was hier steht, ist eine gemeldete
 * BEOBACHTUNG des wt-clients mit Zeitstempel (POST /client/access-findings),
 * kein Live-Test. Die Beschriftung sagt deshalb konsequent "zuletzt geprueft"
 * und nie "erreichbar" im Präsens - eine Meldung von vorgestern beweist
 * nichts über heute.
 *
 * Vier Zustände, und die letzten beiden sind die wichtigen:
 *   ok          - Client kam beim letzten Mal durch
 *   fehlt       - Client kam nicht durch (mit Grund + Abhilfe im Tooltip)
 *   ungeprüft  - noch nie gemeldet. AUSDRÜCKLICH KEIN FEHLER und darf nie
 *                 wie einer aussehen: access_ok === null heißt "wir wissen
 *                 es nicht", nicht "kaputt".
 *   unklar      - GEMELDET, aber ohne Urteil (access_ok null MIT Zeitstempel).
 *                 Befund 09.09.2026: Beides lief hier zusammen unter
 *                 "ungeprueft", samt Tooltip "hat noch keine Zugangsprüfung
 *                 gemeldet" - obwohl der Client gerade eine gemeldet hatte.
 *                 Der Grund (z.B. "ungeprüft - Key-Anmeldung,
 *                 Yubikey-Berührung nötig") stand nur auf der
 *                 Client-Konsole und nirgends im Tool. Ton bleibt neutral:
 *                 ein nicht einzuordnender Befund ist kein Fehler.
 *
 * Bewusst nur ein Hinweis, nie eine Sperre: die Meldung kann veraltet sein,
 * und Colin kennt seine Umgebung besser als das Tool.
 */

/** Die vier Felder, die `System` und `MaintenancePreviewSystem` gemeinsam tragen. */
export interface ZugangsFelder {
  access_checked_at?: string | null;
  access_ok?: boolean | null;
  /** 'ok' | 'auth_denied' | 'no_route' | 'yubikey' | 'hostkey' | 'unknown'. */
  access_code?: string | null;
  access_detail?: string | null;
}

export type ZugangsStatus = 'ok' | 'fehlt' | 'ungeprueft' | 'unklar';

/**
 * `access_ok` entscheidet über das Urteil, `access_checked_at` darüber, ob
 * überhaupt eines vorliegt. Ein Zeitstempel ohne Urteil ist KEINE Aussage
 * über den Zugang - aber sehr wohl eine Meldung, und die zu verschweigen
 * war der Fehler: "unklar" heißt gemeldet, nicht einzuordnen.
 */
export function zugangsStatus(sys: ZugangsFelder): ZugangsStatus {
  if (sys.access_ok === true) return 'ok';
  if (sys.access_ok === false) return 'fehlt';
  return sys.access_checked_at ? 'unklar' : 'ungeprueft';
}

/** Kurztext des Vertrags-Codes (siehe access_code im API-Vertrag). */
const CODE_LABELS: Record<string, string> = {
  ok: 'Zugang stand',
  auth_denied: 'Anmeldung abgelehnt',
  no_route: 'kein Netzweg',
  yubikey: 'Yubikey nicht bestätigt',
  hostkey: 'Host-Key passt nicht',
  unknown: 'Grund unklar',
};

/**
 * Was jetzt zu tun ist. Wortgleich zu den Handlungsanweisungen, die der
 * wt-client selbst auf der Konsole ausgibt (SSH_BEFUNDE in wt-client.py) -
 * zwei verschiedene Anleitungen für dieselbe Lücke wären eine zu viel.
 */
const CODE_ABHILFE: Record<string, string> = {
  auth_denied:
    'Schlüssel hinterlegen (ssh-copy-id <user>@<host>) oder Zugangsdaten in ' +
    'Bitwarden/bw-map pflegen - bei geschlossener Sitzung zuerst bw unlock.',
  no_route:
    'VPN prüfen; danach prüfen, ob SSH auf diesem System überhaupt offen ist ' +
    '(OPNsense: dort meist aus - dann API-Weg statt SSH).',
  yubikey:
    'Yubikey berühren, solange er blinkt. Wenn nichts blinkt: ssh-agent starten ' +
    'und Key laden (ssh-add ~/.ssh/id_ed25519_sk).',
  hostkey:
    'Host-Key prüfen; wenn der Wechsel erwartet war: ssh-keygen -R <host>, ' +
    'danach einmal von Hand verbinden.',
  unknown: 'Von Hand testen: ssh -v <user>@<host>.',
};

export function zugangsCodeLabel(code: string | null | undefined): string {
  if (!code) return 'Grund nicht gemeldet';
  return CODE_LABELS[code] ?? code;
}

/** Abhilfe zum Code; leer, wenn es nichts zu tun gibt (oder der Code fehlt). */
export function zugangsAbhilfe(code: string | null | undefined): string {
  if (!code) return 'Von Hand testen: ssh -v <user>@<host>.';
  return CODE_ABHILFE[code] ?? '';
}

/** Die Client-Meldung kommt roh (z.B. "Permission denied (publickey)") - im
 *  Tooltip steht sie zwischen zwei Sätzen und braucht einen Punkt. */
function alsSatz(text: string): string {
  const roh = text.trim();
  return /[.!?]$/.test(roh) ? roh : `${roh}.`;
}

/**
 * Kompletter Tooltip-Text. Bei fehlendem Zugang steht hier die Abhilfe -
 * genau die Information, die am 13.08. erst nach dem roten Balken kam.
 */
export function zugangsTitel(sys: ZugangsFelder): string {
  const status = zugangsStatus(sys);
  const wann = sys.access_checked_at ? formatDateTime(sys.access_checked_at) : null;
  if (status === 'ungeprueft') {
    return (
      'Noch nie geprüft - das ist kein Fehler. Der wt-client hat für dieses ' +
      'System noch keine Zugangsprüfung gemeldet; der Zugang kann trotzdem stehen.'
    );
  }
  const teile: string[] = [];
  if (status === 'unklar') {
    // Hier steht der Klartext des Clients - ohne ihn wäre die Meldung im
    // Tool unsichtbar (Befund 09.09.2026).
    teile.push(
      wann
        ? `Der wt-client hat am ${wann} gemeldet, konnte den Zugang aber nicht einordnen.`
        : 'Der wt-client hat gemeldet, konnte den Zugang aber nicht einordnen.'
    );
    if (sys.access_detail) teile.push(`Meldung: ${alsSatz(sys.access_detail)}`);
    teile.push(
      'Kein Fehler und keine Sperre - der Lauf startet dort trotzdem; ' +
        'was wirklich vorliegt, zeigt der Step.'
    );
    teile.push('Gemeldete Beobachtung des wt-clients, kein Live-Test.');
    return teile.join(' ');
  }
  if (status === 'ok') {
    teile.push(
      wann
        ? `Zugang stand bei der letzten Prüfung am ${wann}.`
        : 'Zugang stand bei der letzten Prüfung.'
    );
  } else {
    teile.push(
      wann
        ? `Kein Zugang bei der letzten Prüfung am ${wann}: ${zugangsCodeLabel(sys.access_code)}.`
        : `Kein Zugang bei der letzten Prüfung: ${zugangsCodeLabel(sys.access_code)}.`
    );
    if (sys.access_detail) teile.push(`Meldung: ${alsSatz(sys.access_detail)}`);
    const abhilfe = zugangsAbhilfe(sys.access_code);
    if (abhilfe) teile.push(`Abhilfe: ${abhilfe}`);
  }
  // ADR-011 steht bewusst in JEDEM Tooltip: der Server hat das nicht gemessen,
  // er gibt weiter, was der Client gemeldet hat. Eine alte Meldung ist kein
  // Beweis für heute - deshalb bleibt das System wählbar.
  teile.push('Gemeldete Beobachtung des wt-clients, kein Live-Test.');
  return teile.join(' ');
}

function ZugangsIcon({ code, className }: { code: string | null | undefined; className: string }): JSX.Element {
  switch (code) {
    case 'yubikey':
      return <Fingerprint className={className} aria-hidden="true" />;
    case 'auth_denied':
      return <KeyRound className={className} aria-hidden="true" />;
    case 'no_route':
      return <Unplug className={className} aria-hidden="true" />;
    case 'hostkey':
      return <ShieldAlert className={className} aria-hidden="true" />;
    default:
      return <CircleHelp className={className} aria-hidden="true" />;
  }
}

interface BadgeProps {
  system: ZugangsFelder;
  className?: string;
  'data-testid'?: string;
}

/**
 * Eine Marke pro System: Zustand + Grund + Zeitstempel der letzten Meldung.
 *
 * Farben: grün = stand zuletzt, gelb = fehlte zuletzt, neutral = nie
 * geprüft. Kein Rot - fehlender Zugang ist ein Hinweis, kein Schaden.
 */
export function SystemAccessBadge({
  system,
  className,
  'data-testid': testId,
}: BadgeProps): JSX.Element {
  const status = zugangsStatus(system);
  const titel = zugangsTitel(system);

  // "unklar" bleibt bewusst neutral: gemeldet, aber ohne Urteil - kein
  // Warnton für etwas, das niemand als Störung festgestellt hat.
  const tone: BadgeTone =
    status === 'ok' ? 'success' : status === 'fehlt' ? 'warning' : 'neutral';

  const icon =
    status === 'ok' ? (
      <ShieldCheck className="size-3" aria-hidden="true" />
    ) : status === 'fehlt' ? (
      <ZugangsIcon code={system.access_code} className="size-3" />
    ) : status === 'unklar' ? (
      <CircleHelp className="size-3" aria-hidden="true" />
    ) : (
      <CircleDashed className="size-3" aria-hidden="true" />
    );

  const label =
    status === 'ok'
      ? 'Zugang stand'
      : status === 'fehlt'
        ? `kein Zugang · ${zugangsCodeLabel(system.access_code)}`
        : status === 'unklar'
          ? 'gemeldet, nicht einzuordnen'
          : 'noch nie geprüft';

  return (
    <span
      className={['inline-flex flex-wrap items-center gap-1', className ?? ''].join(' ')}
      title={titel}
      data-testid={testId}
      data-access-status={status}
    >
      <Badge tone={tone}>
        <span className="inline-flex items-center gap-1">
          {icon}
          <span>{label}</span>
        </span>
      </Badge>
      {/* Zeitstempel gehört NEBEN die Marke, nicht nur in den Tooltip: eine
          Meldung von letzter Woche ist etwas anderes als eine von heute.
          Gilt auch für "unklar" - dort ist er der Beweis, DASS gemeldet
          wurde. Nur "noch nie geprueft" hat keinen. */}
      {system.access_checked_at && (
        <RelativeTime value={system.access_checked_at} className="text-xs text-muted" />
      )}
    </span>
  );
}

export interface ZugangsZaehler {
  ok: number;
  fehlt: number;
  ungeprueft: number;
  unklar: number;
}

export function zaehleZugang(systeme: readonly ZugangsFelder[]): ZugangsZaehler {
  const out: ZugangsZaehler = { ok: 0, fehlt: 0, ungeprueft: 0, unklar: 0 };
  for (const sys of systeme) {
    const status = zugangsStatus(sys);
    if (status === 'ok') out.ok += 1;
    else if (status === 'fehlt') out.fehlt += 1;
    else if (status === 'unklar') out.unklar += 1;
    else out.ungeprueft += 1;
  }
  return out;
}

interface SummaryProps {
  systems: readonly ZugangsFelder[];
  className?: string;
  'data-testid'?: string;
}

/**
 * Eine Zeile über der Liste. Muss sein, weil die Systemlisten eingeklappt
 * starten: ohne Zusammenfassung sieht Colin die einzelnen Marken erst, wenn
 * er aufklappt - und genau das tut niemand vor dem Start.
 */
export function SystemAccessSummary({
  systems,
  className,
  'data-testid': testId,
}: SummaryProps): JSX.Element | null {
  if (systems.length === 0) return null;
  const z = zaehleZugang(systems);
  const klasse = ['text-xs', className ?? ''].join(' ');

  // Solange der Client noch nichts gemeldet hat, ist ALLES ungeprüft. Eine
  // Zeile "0 in Ordnung" wäre dann die Dauer-Anzeige und läse sich wie ein
  // Befund, wo gar keiner erhoben wurde.
  if (z.ok === 0 && z.fehlt === 0 && z.unklar === 0) {
    return (
      <p className={`${klasse} text-muted`} data-testid={testId}>
        Zugang:{' '}
        {z.ungeprueft === 1 ? 'dieses System' : `alle ${z.ungeprueft} Systeme`} noch nie
        geprüft &ndash; kein Fehler, der wt-client hat dazu noch nichts gemeldet.
      </p>
    );
  }

  const teile: JSX.Element[] = [];
  if (z.ok > 0) {
    teile.push(
      <span key="ok" className="text-muted">
        {z.ok} in Ordnung
      </span>
    );
  }
  if (z.fehlt > 0) {
    teile.push(
      <span key="fehlt" className="font-medium text-warning">
        {z.fehlt} ohne Zugang{' '}
        {z.fehlt === 1 ? '(Lauf würde dort blockieren)' : '(Läufe würden dort blockieren)'}
      </span>
    );
  }
  if (z.unklar > 0) {
    teile.push(
      <span key="unklar" className="text-muted">
        {z.unklar} gemeldet, nicht einzuordnen
      </span>
    );
  }
  if (z.ungeprueft > 0) {
    teile.push(
      <span key="ungeprueft" className="text-muted">
        {z.ungeprueft} noch nie geprüft
      </span>
    );
  }

  return (
    <p className={klasse} data-testid={testId}>
      <span className="text-muted">Zugang zuletzt: </span>
      {teile.map((teil, i) => (
        <Fragment key={teil.key}>
          {i > 0 && <span className="text-muted"> · </span>}
          {teil}
        </Fragment>
      ))}
    </p>
  );
}
